import type * as React from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
	type ZodDiscriminatedUnion,
	type ZodEnum,
	type ZodObject,
	ZodString,
} from "zod";

import FormSelect from "../../components/FormSelect";
import ProgramFormDynamicInput from "./ProgramFormDynamicInput";
import { camelToTitle } from "~/utils/helpers";

interface ProgramFormUnionInputProps {
	schema: ZodDiscriminatedUnion<ZodObject[]>;
	formKey: string;
}

const ProgramFormUnionInput: React.FC<ProgramFormUnionInputProps> = ({
	formKey,
	schema,
}) => {
	const discriminatorKey = schema.def.discriminator;

	const discriminator = schema.def.options[1]?.shape[
		discriminatorKey
	] as ZodEnum;

	discriminator.options.push(
		...(schema.def.options[0]?.shape[discriminatorKey]?.options.filter(
			(option: any) => !discriminator.options.includes(option),
		) || []),
	);

	const { watch, setValue } = useFormContext();

	const discrimatorFormKey = formKey + "." + discriminatorKey;

	const selectedDiscriminator = watch(discrimatorFormKey) as string;

	const discriminatedFields = schema.def.options.find((option) =>
		option.shape[discriminatorKey].options.includes(selectedDiscriminator),
	)?.shape;

	useEffect(() => {
		setValue(formKey, {
			[discriminatorKey]: selectedDiscriminator,
		});
	}, [discriminatorKey, formKey, selectedDiscriminator, setValue]);

	return (
		<div className="space-y-4">
			<FormSelect
				formKey={discrimatorFormKey}
				schema={discriminator as ZodEnum}
			/>

			{discriminatedFields &&
				Object.keys(discriminatedFields).map((key) => {
					if (key === discriminatorKey) return null;
					const fieldSchema = discriminatedFields[key];
					if (fieldSchema instanceof ZodString) {
						return (
							<ProgramFormDynamicInput
								key={key}
								formKey={formKey + "." + key}
								schema={fieldSchema}
								label={
									(schema as any).meta?.title ?? camelToTitle(key.toString())
								}
							/>
						);
					}
					return null; // Handle other schema types as needed
				})}
		</div>
	);
};

export default ProgramFormUnionInput;
